import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddressField } from "@/components/AddressAutocomplete";
import { CommunityPreviewCard } from "@/components/CommunityPreviewCard";
import { toast } from "sonner";
import { Building2, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/contexts/ThemeContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("");
  const [communityId, setCommunityId] = useState("");
  const [address, setAddress] = useState("");
  const [addressVerified, setAddressVerified] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();

  // Fetch communities once for the register dropdown.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/onboarding/communities`);
        setCommunities(data?.items || []);
      } catch (err) { if (typeof console !== 'undefined') console.debug('pages/LoginPage.jsx: swallowed', err); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let res;
    if (tab === "login") {
      res = await login(email, password);
    } else {
      if (!communityId) {
        setLoading(false);
        toast.error("Please pick your community");
        return;
      }
      res = await register({
        email, password, name, unit,
        community_id: communityId,
        address: address.trim() || null,
      });
    }
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || "Authentication failed");
      return;
    }
    toast.success("Welcome");
    navigate(res.user.role === "admin" ? "/admin/dashboard" : "/resident/dashboard");
  };

  const fillDemo = (role) => {
    if (role === "admin") { setEmail("cmv92570@gmail.com"); setPassword("Admin@2026"); }
    else if (role === "resident") { setEmail("resident@prestigious.com"); setPassword("Resident@2026"); }
    else { setEmail("vendor@acme-hvac.com"); setPassword("Vendor@2026"); }
    setTab("login");
  };

  return (
    <div className={"min-h-screen flex relative " +
      (theme === "dark"
        ? "bg-[#050505] grid-lines-dark text-white"
        : "bg-[#F5F6F7] text-zinc-900")}>
      {/* Theme toggle — floats top-right so unauth users can flip it too. */}
      <div className="absolute top-4 right-4 z-40">
        <ThemeToggle variant={theme === "dark" ? "default" : "light"} />
      </div>
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1621478493131-8597c4d4f5b4?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-black/40" />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <img src="/prestigious-logo.png" alt="Prestigious Property Management Services" className="h-16 w-16 drop-shadow-2xl" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-orange-300/90">HOA · COA · PM Portal</div>
            </div>
          </div>
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="text-5xl font-semibold font-display leading-[1.05] tracking-tight"
            >
              The operating system<br />for every property.
            </motion.h1>
            <p className="text-zinc-300 mt-6 max-w-md text-[15px] leading-relaxed">
              Delinquency, maintenance, ARC, amenities, access, insurance, and vendors under one command console with AI throughout.
            </p>
            <div className="mt-8 flex items-center gap-2 text-xs text-indigo-300">
              <Sparkles className="h-3.5 w-3.5" /> Powered by Claude Sonnet 4.5
            </div>
          </div>
          <div className="text-xs text-zinc-500">© 2026 Prestigious Property Management Services LLC</div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-[#0a0a0b] border-white/10 text-white p-8">
          <div className="mb-6">
            <div className="overline mb-2">{tab === "login" ? "Sign in" : "New resident"}</div>
            <h2 className="text-2xl font-semibold font-display">{tab === "login" ? "Welcome back" : "Create your account"}</h2>
            <p className="text-sm text-zinc-400 mt-1">
              {tab === "login" ? "Access your admin console or resident portal." : "Join your community in under a minute."}
            </p>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 bg-white/[0.04] border border-white/10 h-auto p-1 rounded-lg">
              <TabsTrigger value="login" data-testid="tab-login" className="data-[state=active]:bg-white data-[state=active]:text-black">Log in</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register" className="data-[state=active]:bg-white data-[state=active]:text-black">Create account</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {tab === "register" && (
                <>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-400">Full name</Label>
                    <Input data-testid="reg-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 bg-white/[0.04] border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-400">Community</Label>
                    <Select value={communityId} onValueChange={setCommunityId}>
                      <SelectTrigger
                        data-testid="reg-community"
                        className="mt-1 bg-white/[0.04] border-white/10 text-white data-[placeholder]:text-zinc-500"
                      >
                        <SelectValue placeholder={communities.length ? "Pick your community" : "Loading communities…"} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                        {communities.map((c) => (
                          <SelectItem
                            key={c.id}
                            value={c.id}
                            data-testid={`reg-community-opt-${c.id}`}
                            className="focus:bg-white/[0.06] focus:text-white"
                          >
                            <div className="flex flex-col">
                              <span>{c.name}</span>
                              {c.address && <span className="text-[11px] text-zinc-500">{c.address}</span>}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <CommunityPreviewCard community={communities.find((c) => c.id === communityId)} />
                  </div>
                  <AddressField
                    value={address}
                    onChange={(v) => { setAddress(v); setAddressVerified(false); }}
                    onPicked={() => setAddressVerified(true)}
                    verified={addressVerified}
                    placeholder="Start typing… we'll suggest addresses"
                    hint={addressVerified ? "Address verified against OpenStreetMap." : "Where should we send physical mail (statements, HOA notices)?"}
                    testIdRoot="reg-address"
                  />
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-400">Unit</Label>
                    <Input data-testid="reg-unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="e.g. 12B" className="mt-1 bg-white/[0.04] border-white/10 text-white" />
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
                <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-white/[0.04] border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Password</Label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 bg-white/[0.04] border-white/10 text-white" />
              </div>
              <Button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-full h-11"
              >
                {loading ? "…" : tab === "login" ? "Sign in" : "Create account"}
              </Button>
            </form>
          </Tabs>

          <div className="mt-4 text-center text-sm text-zinc-400 space-y-1">
            <div>
              <Link to="/forgot-password" className="text-orange-300 hover:text-orange-200 underline" data-testid="login-forgot-link">
                Forgot your password?
              </Link>
            </div>
            <div>
              Running an HOA?{" "}
              <Link to="/signup" className="text-orange-300 hover:text-orange-200 underline" data-testid="login-signup-link">
                Start a free trial
              </Link>
            </div>
          </div>

          <div className="mt-6 border-t border-white/10 pt-5">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Try a demo account</div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" size="sm" data-testid="demo-admin" onClick={() => fillDemo("admin")} className="border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white">Admin</Button>
              <Button variant="outline" size="sm" data-testid="demo-resident" onClick={() => fillDemo("resident")} className="border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white">Resident</Button>
              <Button variant="outline" size="sm" data-testid="demo-vendor" onClick={() => fillDemo("vendor")} className="border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white">Vendor</Button>
            </div>
            <div className="text-[11px] text-zinc-500 mt-3">
              Admin: cmv92570@gmail.com / Admin@2026<br />
              Resident: resident@prestigious.com / Resident@2026
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-center gap-4 text-[11px] text-zinc-500">
            <Link to="/terms" className="hover:text-zinc-300" data-testid="login-footer-terms">Terms</Link>
            <span className="opacity-30">·</span>
            <Link to="/privacy" className="hover:text-zinc-300" data-testid="login-footer-privacy">Privacy</Link>
            <span className="opacity-30">·</span>
            <Link to="/legal/downloads" className="hover:text-zinc-300" data-testid="login-footer-legal">Legal</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
